// schema
import { endOfDay, startOfDay, subDays, subMonths } from "date-fns";
import produce from "immer";
import create from "zustand";

import { IEngagementCustomFilterValue, IEngagementDefaultFilterValue, IEngagementFilter } from "../schema";

export interface EngagementStore {
	filter: IEngagementFilter;
	setFilter: (
		type: "default" | "custom",
		filter: IEngagementDefaultFilterValue | IEngagementCustomFilterValue
	) => void;
	overview: {
		// Month
		month: Date | null;
		setMonth: (month: Date | null) => void;
		// Cohort
		cohort: string | null;
		setCohort: (cohort: string | null) => void;
	};
	cohorts: {
		// selection variables
		// Month
		month: Date | null;
		setMonth: (month: Date | null) => void;
		// Cohort ID
		cohort: string | null;
		setCohort: (cohort: string | null) => void;
	};
	factors: {
		// selection variables
		// Month
		month: Date | null;
		setMonth: (month: Date | null) => void;
		// Cohort ID
		cohort: string | null;
		setCohort: (cohort: string | null) => void;
	};
	eNPS: {
		// selection variables
		// Month
		month: Date | null;
		setMonth: (month: Date | null) => void;
		// Cohort ID
		cohort: string | null;
		setCohort: (cohort: string | null) => void;
	};
}

/**
 * This is the store for the Engagement pages.
 */
const store = create<EngagementStore>((set) => ({
	filter: {
		value: "all",
		startDate: null,
		endDate: null,
	},
	setFilter: (type, filter) => {
		if (type === "default") {
			let startDate: Date | null = null;
			// derive start date
			switch (filter) {
				case "30":
					startDate = startOfDay(subDays(new Date(), 30));
					break;
				case "90":
					startDate = startOfDay(subDays(new Date(), 90));
					break;
				case "year":
					startDate = startOfDay(subMonths(new Date(), 12));
					break;
				case "all":
				default:
					startDate = null;
					break;
			}
			set(
				produce((draft) => {
					draft.filter.value = filter;
					draft.filter.startDate = startDate;
				})
			);
		}
		if (type === "custom") {
			const customFilter = filter as IEngagementCustomFilterValue;
			set(
				produce((draft) => {
					const startDate = startOfDay(new Date(customFilter.from));
					const endDate = endOfDay(new Date(customFilter.to));
					draft.filter.value = {
						from: startDate,
						to: endDate,
					};
					draft.filter.startDate = startDate;
					draft.filter.endDate = endDate;
				})
			);
		}
	},
	overview: {
		month: null,
		setMonth: (month) => {
			set(
				produce((draft) => {
					draft.overview.month = month;
				})
			);
		},
		cohort: null,
		setCohort: (cohort) => {
			set(
				produce((draft) => {
					draft.overview.cohort = cohort;
				})
			);
		},
	},
	cohorts: {
		// page variables
		month: null,
		setMonth: (month) => {
			set(
				produce((draft) => {
					draft.cohorts.month = month;
				})
			);
		},
		cohort: null,
		setCohort: (cohort) => {
			set(
				produce((draft) => {
					draft.cohorts.cohort = cohort;
				})
			);
		},
	},
	factors: {
		// page variables
		month: null,
		setMonth: (month) => {
			set(
				produce((draft) => {
					draft.factors.month = month;
				})
			);
		},
		cohort: null,
		setCohort: (cohort) => {
			set(
				produce((draft) => {
					draft.factors.cohort = cohort;
				})
			);
		},
	},
	eNPS: {
		// page variables
		month: null,
		setMonth: (month) => {
			set(
				produce((draft) => {
					draft.eNPS.month = month;
				})
			);
		},
		cohort: null,
		setCohort: (cohort) => {
			set(
				produce((draft) => {
					draft.eNPS.cohort = cohort;
				})
			);
		},
	},
}));

export default store;
